/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import numeral from 'numeral';

import BonusIcon from 'app/components/icons/BonusIcon';
import Icon from '../commons/Icon';
import MatchUpLine from './MatchUpLine';
import PlayerLabels from './PlayerLabels';

type Props = {
  awayTeam?: string,
  average?: number,
  bonus?: {
    name?: string,
    key?: string
  },
  extra?: string,
  form?: number | string,
  gameOrder?: number,
  highlight?: boolean,
  homeTeam?: string,
  isCaptain?: boolean,
  isHome: boolean,
  isRacing?: boolean,
  isScratched?: boolean,
  labels?: Array<Object>,
  name: string,
  number?: number,
  position?: string,
  progress?: string,
  reverseOrder?: boolean,
  rider?: string,
  trainer?: string,
  layout?: 'default' | 'reverse'
};

const defaultProps = {
  awayTeam: '',
  average: undefined,
  bonus: undefined,
  extra: undefined,
  form: undefined,
  gameOrder: undefined,
  isCaptain: false,
  isRacing: false,
  isScratched: false,
  labels: [],
  highlight: false,
  homeTeam: '',
  number: undefined,
  position: undefined,
  progress: undefined,
  reverseOrder: false,
  rider: undefined,
  trainer: undefined,
  layout: 'default'
};

const PlayerBarPrimary = styled.div(
  {
    display: 'flex',
    flexBasis: 0,
    flexDirection: 'column',
    flexGrow: 1,
    flexShrink: 1,
    justifyContent: 'space-between',
    minWidth: 0, // flexbox hack
    paddingLeft: '0.25em',
    paddingRight: '0.5em'
  },
  ({ theme }) => ({
    paddingTop: theme.spacing.xsmall,
    paddingBottom: theme.spacing.xsmall
  })
);

const PlayerBarTitle = styled.div({
  alignItems: 'center',
  display: 'flex'
});

const IconSpacer = styled.span({
  display: 'inline-block',
  paddingRight: 5
});

const PlayerBarName = styled.div(
  {
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    transition: 'color 0.3s'
  },
  ({ theme }) => ({
    fontWeight: theme.fontWeight.demiBold
  }),
  ({ isScratched }) => (isScratched ? { textDecoration: 'line-through' } : {}),
  ({ theme, highlight }) =>
    highlight
      ? {
          color: theme.color.primary
        }
      : {}
);

const PlayerBarMeta = styled.div({
  alignItems: 'center',
  fontSize: '0.833em',
  overflow: 'visible',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});

const PlayerBarDetails = styled.div(({ layout }) => ({
  paddingRight: '0.25em',
  display: 'flex',
  justifyContent: layout === 'reverse' ? 'flex-end' : 'flex-start'
}));

const PlayerLabelsWrapper = styled.span({
  paddingRight: '0.25em'
});

const PlayerBarPosition = styled.span({});

const PlayerBarExtra = styled.span(({ theme }) => ({
  textTransform: 'uppercase',
  '&::before': {
    color: theme.color.basis2,
    content: '" | "'
  }
}));

const PlayerBarStage = styled.span({
  display: 'inline-block'
});

const PositionAndGame = styled.div({
  display: 'flex',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
});

const JerseyNumber = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const Divider = styled.span({
  padding: '0 0.3em',
  '&::after': {
    content: '" | "'
  }
});

const Stats = styled.div(({ paddingLeft }) => ({
  paddingLeft: paddingLeft ? '1.2em' : 0,
  paddingRight: '1.2em'
}));

const StatsLabel = styled.span(({ theme }) => ({
  paddingRight: theme.spacing.tiny,
  fontWeight: theme.fontWeight.demiBold
}));

const StatsValue = styled.span({});

function PlayerBar({
  awayTeam,
  average,
  bonus,
  extra,
  form,
  gameOrder,
  highlight,
  homeTeam,
  isCaptain,
  isHome,
  isRacing,
  isScratched,
  labels,
  name,
  number,
  position,
  progress,
  reverseOrder,
  rider,
  trainer,
  layout
}: Props) {
  const renderStats = () => (
    <React.Fragment>
      {form !== undefined && (
        <Stats paddingLeft>
          <StatsLabel>FORM</StatsLabel>
          <StatsValue>{numeral(form).format('0.0')}</StatsValue>
        </Stats>
      )}
      {average !== undefined && (
        <Stats paddingRight>
          <StatsLabel>AVG</StatsLabel>
          <StatsValue>{numeral(average).format('0.0')}</StatsValue>
        </Stats>
      )}
    </React.Fragment>
  );

  return (
    <PlayerBarPrimary>
      <PlayerBarTitle>
        {isCaptain && (
          <IconSpacer>
            <Icon type="icon-captain" size={18} />
          </IconSpacer>
        )}
        {bonus && bonus.key && (
          <IconSpacer>
            <BonusIcon id={bonus.key} name={bonus.name} />
          </IconSpacer>
        )}
        <PlayerBarName highlight={highlight} isScratched={isScratched}>
          {name}
        </PlayerBarName>
      </PlayerBarTitle>
      <PlayerBarMeta>
        <PlayerBarDetails layout={layout}>
          {layout === 'reverse' && !isRacing && renderStats()}
          <PositionAndGame>
            {isRacing && gameOrder && (
              <JerseyNumber>
                <span>L{gameOrder}</span>
              </JerseyNumber>
            )}
            {isRacing && rider && (
              <React.Fragment>
                <Divider />
                <div>
                  <StatsLabel>J</StatsLabel>
                  <span>{rider}</span>
                </div>
              </React.Fragment>
            )}
            {isRacing && trainer && (
              <React.Fragment>
                <Divider />
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <StatsLabel>T</StatsLabel>
                  <span>{trainer}</span>
                </div>
              </React.Fragment>
            )}
            {!isRacing && number !== undefined && (
              <React.Fragment>
                <JerseyNumber>
                  <Icon type="icon-jersey" />
                  <span style={{ paddingLeft: '0.1em' }}>{number}</span>
                </JerseyNumber>
                <Divider />
              </React.Fragment>
            )}
            {!isRacing && position && <PlayerBarPosition>{position}</PlayerBarPosition>}
            {!isRacing && awayTeam && homeTeam && (
              <React.Fragment>
                <Divider />
                <MatchUpLine
                  awayTeam={awayTeam}
                  homeTeam={homeTeam}
                  highlightAway={!isHome}
                  highlightHome={isHome}
                  reverseOrder={reverseOrder}
                />
              </React.Fragment>
            )}
          </PositionAndGame>
          {layout === 'default' && !isRacing && renderStats()}
          {Array.isArray(labels) && labels.length > 0 && (
            <PlayerLabelsWrapper>
              <PlayerLabels items={labels} />
            </PlayerLabelsWrapper>
          )}
          {extra && <PlayerBarExtra>{extra}</PlayerBarExtra>}
          {progress && <PlayerBarStage>{progress}</PlayerBarStage>}
        </PlayerBarDetails>
      </PlayerBarMeta>
    </PlayerBarPrimary>
  );
}

PlayerBar.defaultProps = defaultProps;

export default PlayerBar;
